import type { ProvenanceDetailResponse } from '@vivport/backend/types';
import { Badge } from '@/components/ui/badge';
import { useUser } from '@/hooks/useUser';
import type { ManualSessionContent, ProvenanceContent } from './provenance-content';

export interface ProvenanceSummaryHeaderProps {
  content: ProvenanceContent;
  detail?: ProvenanceDetailResponse | null;
  isLoadingDetail?: boolean;
  onNavigate?: (href: string) => void;
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) {
    return 'N/A';
  }
  return new Date(value).toLocaleString();
}

export function ProvenanceSummaryHeader({
  content,
  detail,
  isLoadingDetail,
  onNavigate,
}: ProvenanceSummaryHeaderProps): JSX.Element | null {
  switch (content.contentType) {
    case 'NEO_CHAT':
      return null;
    case 'PROCESS_RUN': {
      const subtype =
        detail?.subtypeData.kind === 'PROCESS_RUN' ? detail.subtypeData : null;
      return (
        <ProcessRunHeader
          content={content}
          detail={detail ?? null}
          subtype={subtype}
          isLoading={isLoadingDetail ?? false}
          onNavigate={onNavigate}
        />
      );
    }
    case 'MANUAL_SESSION':
      return <ManualSessionHeader content={content} detail={detail ?? null} />;
  }
}

interface ProcessRunHeaderProps {
  content: Extract<ProvenanceContent, { contentType: 'PROCESS_RUN' }>;
  detail: ProvenanceDetailResponse | null;
  subtype: Extract<ProvenanceDetailResponse['subtypeData'], { kind: 'PROCESS_RUN' }> | null;
  isLoading: boolean;
  onNavigate?: (href: string) => void;
}

function ProcessRunHeader({
  content,
  detail,
  subtype,
  isLoading,
  onNavigate,
}: ProcessRunHeaderProps) {
  const { data: memberContext } = useUser();
  const isHubAdmin = memberContext?.isHubAdmin ?? false;
  const status = detail?.status ?? content.status;
  const processIdentity = content.processId
    ? `Process ID: ${content.processId}`
    : 'Process ID: N/A';
  const work4MeHref = content.processId
    ? `/work4me/processes/${encodeURIComponent(content.processId)}?run=${content.id}`
    : null;
  const missionControlHref = isHubAdmin && subtype?.agentRunId
    ? `/admin/mission-control?mcTab=runs&runId=${encodeURIComponent(subtype.agentRunId)}`
    : null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-medium">{content.processNameSnapshot || 'Process run'}</h3>
        <Badge variant="outline">{status}</Badge>
      </div>
      <p className="text-sm text-muted-foreground">
        Run code: {content.code} | Agent: {content.agentKeySnapshot} | {processIdentity}
      </p>
      {subtype ? (
        <p className="text-sm text-muted-foreground">
          Trigger: {subtype.triggerType ?? 'N/A'} | Queued: {formatTimestamp(subtype.queuedAt)} |
          Started: {formatTimestamp(subtype.startedAt)} | Finished:{' '}
          {formatTimestamp(subtype.finishedAt)} | Attempts: {subtype.attempts ?? 0}
        </p>
      ) : isLoading ? (
        <p className="text-sm text-muted-foreground">Loading run metadata…</p>
      ) : (
        <p className="text-sm text-muted-foreground">Run metadata unavailable.</p>
      )}
      {subtype?.error ? <p className="text-sm text-destructive">Error: {subtype.error}</p> : null}
      {work4MeHref || missionControlHref ? (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {work4MeHref ? (
            <button
              type="button"
              className="text-primary underline"
              onClick={() => onNavigate?.(work4MeHref)}
            >
              View in Work4Me Processes
            </button>
          ) : null}
          {missionControlHref ? (
            <button
              type="button"
              className="text-primary underline"
              onClick={() => onNavigate?.(missionControlHref)}
            >
              Agent execution trace (Mission Control)
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

interface ManualSessionHeaderProps {
  content: ManualSessionContent;
  detail: ProvenanceDetailResponse | null;
}

function ManualSessionHeader({ content, detail }: ManualSessionHeaderProps) {
  const status = detail?.closedAt == null ? 'Active' : 'Closed';
  const closeReason = detail?.closeReason ?? null;
  const startedAt = detail?.createdAt ?? content.createdAt;
  const closedAt = detail?.closedAt ?? null;
  const entryCount = detail?.entries.length ?? 0;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-medium">{content.title?.trim() || 'Manual session'}</h3>
        <Badge variant="outline">{status}</Badge>
      </div>
      <p className="text-sm text-muted-foreground">
        Code: {content.code} | Entries: {entryCount}
        {closeReason ? ` | Reason: ${closeReason}` : ''}
      </p>
      <p className="text-sm text-muted-foreground">
        Started: {formatTimestamp(startedAt)}
        {closedAt ? ` | Closed: ${formatTimestamp(closedAt)}` : ''}
      </p>
    </div>
  );
}
